import { CommandType } from '~/protos/agents'
import { ConversationWithMessages } from '~/types/Conversations'
import { BillingInfo } from '~/types/ResponseType'
import { fetchWrapper } from '~/utils/fetchWrapper'

export const getSuperAdminHeartbeat = async () => {
  const path = '/api/super-admin/heartbeat'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get super admin heartbeat. ${reason}`)
  }
}

export const getSignupsCSV = async (queryParams?: string): Promise<Blob> => {
  const url = queryParams
    ? `/api/super-admin/csv/signups?${queryParams}`
    : '/api/super-admin/csv/signups'

  const response = await fetchWrapper(url, {
    headers: {
      Accept: 'text/csv'
    }
  })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to download signups CSV. ${reason}`)
  }

  return response.blob()
}

export const getUserActivitiesCSV = async (queryParams?: string): Promise<Blob> => {
  const url = queryParams
    ? `/api/super-admin/csv/user-activities?${queryParams}`
    : '/api/super-admin/csv/user-activities'

  const response = await fetchWrapper(url, {
    headers: {
      Accept: 'text/csv'
    }
  })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to download user activities CSV. ${reason}`)
  }

  return response.blob()
}

export const getLastActiveUsersCSV = async (queryParams?: string): Promise<Blob> => {
  const url = queryParams
    ? `/api/super-admin/csv/last-active-users?${queryParams}`
    : '/api/super-admin/csv/last-active-users'

  const response = await fetchWrapper(url, {
    headers: {
      Accept: 'text/csv'
    }
  })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to download last active users CSV. ${reason}`)
  }

  return response.blob()
}

export const getUserTaskSubmission = async (queryParams?: string): Promise<Blob> => {
  const url = queryParams
    ? `/api/super-admin/csv/user-task-submissions?${queryParams}`
    : '/api/super-admin/csv/user-task-submissions'

  const response = await fetchWrapper(url, {
    headers: {
      Accept: 'text/csv'
    }
  })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to download user activities CSV. ${reason}`)
  }

  return response.blob()
}

export const getAccessLogsByEmailCSV = async (queryParams: string): Promise<Blob> => {
  const url = `/api/super-admin/lookup/email-access-logs?${queryParams}`
  const response = await fetchWrapper(url, {
    headers: {
      Accept: 'text/csv'
    }
  })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to download access logs by email CSV. ${reason}`)
  }
  return response.blob()
}

export const getUserByUuid = async (queryParams: string): Promise<any> => {
  const url = `/api/super-admin/lookup/uuid-user?${queryParams}`
  const response = await fetchWrapper(url, {
    headers: {
      Accept: 'text/csv'
    }
  })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to get email. ${reason}`)
  }
  return response.blob()
}

export const getSettingsCampaignAgentType = async () => {
  const path = '/api/super-admin/settings/campaigns-agent-type'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get campaign agent type settings. ${reason}`)
  }
  return response.json()
}

export const updateSettingsCampaignAgentType = async (payload: { agentType: CommandType }) => {
  const { agentType } = payload
  const path = '/api/super-admin/settings/campaigns-agent-type'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ agentType })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update campaign agent type settings. ${reason}`)
  }
}

export const getBillingList = async () => {
  const path = '/api/super-admin/billingList'
  const response = await fetchWrapper(path, { method: 'GET' })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get billing list. ${reason}`)
  }
  return response.json()
}

export const updateAccountType = async (BillingInfo: BillingInfo) => {
  const path = '/api/super-admin/billing/update-billing-type'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(BillingInfo)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update billing type. ${reason}`)
  }
}

export const getTasksDomainList = async () => {
  const path = '/api/super-admin/tasks/domains'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get tasks domain list. ${reason}`)
  }
  return response.json()
}

export const getTasksEmailList = async (domain: string) => {
  const path = `/api/super-admin/tasks/emails?domain=${encodeURIComponent(domain)}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get tasks email list. ${reason}`)
  }
  return response.json()
}

export const getTasksWorkspaceList = async (email: string) => {
  const path = `/api/super-admin/tasks/workspaces?email=${encodeURIComponent(email)}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get tasks workspace list. ${reason}`)
  }
  return response.json()
}

export const getConversationByUuid = async (uuid: string) => {
  const path = `/api/conversation/${uuid}`
  const response = await fetchWrapper(path, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get conversation details. Reason: ${reason}`)
  }
  const data = await response.json()
  return data as ConversationWithMessages
}

export const getRecentInactiveUsers = async (queryParams?: string) => {
  const url = queryParams
    ? `/api/super-admin/csv/recent-inactive-users?${queryParams}`
    : '/api/super-admin/csv/recent-inactive-users'

  const response = await fetchWrapper(url)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get recent inactive users. ${reason}`)
  }

  return response.blob()
}

export const getInactiveUsers = async (queryParams?: string) => {
  const url = queryParams
    ? `/api/super-admin/csv/inactive-users?${queryParams}`
    : '/api/super-admin/csv/inactive-users'

  const response = await fetchWrapper(url)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get inactive users. ${reason}`)
  }

  return response.blob()
}

export const getTestDriveEmails = async (queryParams?: string) => {
  const url = queryParams
    ? `/api/super-admin/csv/test-drive-emails?${queryParams}`
    : '/api/super-admin/csv/test-drive-emails'

  const response = await fetchWrapper(url)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get test drive emails. ${reason}`)
  }

  return response.blob()
}

export const getTestDriveUsersTaskSubmissionCount = async (queryParams?: string) => {
  const url = queryParams
    ? `/api/super-admin/csv/test-drive-emails-submission-count?${queryParams}`
    : '/api/super-admin/csv/test-drive-emails-submission-count'

  const response = await fetchWrapper(url)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get test drive users task submission count. ${reason}`)
  }

  return response.blob()
}
